import { useEffect, useCallback, useRef } from "react";

import { jwtDecode } from "jwt-decode";
import {
  AccountStatus,
  IsInitLoginedContextProvider,
} from "provider/isInitLoginedContextProvider";
import { decodeToken } from "react-jwt";
import { useLocation, useNavigate } from "react-router-dom";

import {
  LoginBodiesAuthFlow,
  useLoginHook,
  useVerifyHook,
} from "../api-client";
interface CustomTokenPayload {
  "cognito:groups"?: string[];
  sub?: string;
}
export const Session = () => {
  const navigate = useNavigate();
  const idToken = localStorage.getItem("idToken");
  const refreshToken = localStorage.getItem("refreshToken")
  const location = useLocation();
  const decodedIdToken = idToken ? decodeToken<any>(idToken) : '';
  const [clinicAccountStatus, clinicStatus] = IsInitLoginedContextProvider();
  const isRefreshingRef = useRef(false);

  const extentionRefreshToken = async () => {
    if (isRefreshingRef.current) {
      return;
    }
    isRefreshingRef.current = true;
    try {
      const login = useLoginHook();

      const result = await login({
        refreshToken: refreshToken!,
        authFlow: LoginBodiesAuthFlow.REFRESH_TOKEN_AUTH,
      });
      if (!result?.data?.idToken && !result?.data?.accessToken && !result?.data?.refreshToken) {
        localStorage.clear();
        navigate("/login");
        return;
      }

      if (result?.data?.idToken) {
        //updateIdToken(result.data.idToken);
        localStorage.idToken = result.data.idToken;
      }
      if (result?.data?.accessToken) {
        //updateAccessToken(result.data.accessToken);
        localStorage.accessToken = result.data.accessToken;
      }
      if (result?.data?.refreshToken) {
        //updateRefreshToken(result.data.refreshToken);
        localStorage.refreshToken = result.data.refreshToken;
      }
    } catch (e) {
      localStorage.clear();
      navigate("/login");
    } finally {
      isRefreshingRef.current = false;
    }
  };

  const observeAuth = useCallback(async () => {
    if (!idToken) {
      localStorage.clear();
      return;
    }

    try {
      const verify = useVerifyHook();
      await verify();

      if (refreshToken) {
        // トークンの有効期限 (exp) を秒単位で取得
        const expirationTimestamp = decodedIdToken?.exp!; // トークンの有効期限 (exp) のタイムスタンプ
        // 現在の時間 (Unix エポックからの経過秒数)
        const currentTimestamp = Math.floor(Date.now() / 1000);
        // トークンの有効期限までの残り秒数を計算
        const remainingSeconds = expirationTimestamp - currentTimestamp;
        // 残り秒数を分単位に変換
        const remainingIdTokenMinutes = Math.floor(remainingSeconds / 60);

        if (remainingIdTokenMinutes <= 5) {
          extentionRefreshToken();
          return;
        }
      }
    } catch (e: any) {
      // JWTが有効期限切れ or 無効
      // refreshToken があればリフレッシュで自動復帰を試みる
      if (refreshToken) {
        await extentionRefreshToken();
        return;
      }
      // refreshToken がない場合はログアウト
      localStorage.clear();
      navigate("/login");
      return;
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (location.state?.isInitDone) {
      return;
    }
    const idToken = localStorage.getItem("idToken");

    if (!idToken) return;
    const decodedToken = jwtDecode<CustomTokenPayload & { exp?: number }>(idToken);
    const exp = decodedToken?.exp;
    const nowSec = Math.floor(Date.now() / 1000);
    // 期限切れトークンでのルーティング判定を避ける（リフレッシュ完了を待つ）
    if (typeof exp === "number" && exp <= nowSec) {
      return;
    }
    const roles = decodedToken["cognito:groups"];
    const id = decodedToken.sub;
    localStorage.setItem("roles", roles![0]);
    localStorage.setItem("id", id!);

    if (!roles) return;
    // スタッフは設定不要のため処理をスキップ
    if (roles.includes("staff")) {
      return;
    }
    // MEMO: 通常ページを開いた時に、初期設定が完了している場場合は、初期設定ページに遷移させない
    if (
      location.pathname !== "/initial-setting" &&
      roles.includes("owner") &&
      clinicAccountStatus === AccountStatus.done &&
      clinicStatus === AccountStatus.done
    ) {
      return;
    }

    if (
      location.pathname !== "/initial-setting" &&
      !roles.includes("owner") &&
      clinicAccountStatus === AccountStatus.done
    ) {
      return;
    }

    if (
      roles.includes("owner") &&
      location.pathname === "/initial-setting" &&
      clinicAccountStatus === AccountStatus.done &&
      clinicStatus === AccountStatus.done
    ) {
      navigate("/");
      return;
    }

    if (
      !roles.includes("owner") &&
      location.pathname === "/initial-setting" &&
      clinicAccountStatus === AccountStatus.done
    ) {
      navigate("/");
      return;
    }

    // MEMO: ループ防止
    if (
      location.pathname === "/initial-setting" ||
      location.pathname === "/login"
    )
      return;
    if (
      roles.includes("owner") &&
      !(
        clinicAccountStatus === AccountStatus.done &&
        clinicStatus === AccountStatus.done
      )
    ) {
      navigate("/initial-setting");
      return;
    }
    if (
      !roles.includes("owner") &&
      clinicAccountStatus !== AccountStatus.done
    ) {
      navigate("/initial-setting");
      return;
    }
    navigate(location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicAccountStatus, clinicStatus, location.pathname, navigate]);

  useEffect(() => {
    observeAuth();
    // eslint-disable-next-line
  }, [observeAuth]);

  return <>{null}</>;
};
